import { GpsConfig } from '@/lib/config';
import { logQueueService } from '@/services/logQueue';
import { assignmentRepo } from '@/data/repositories/assignmentRepo';
import { LogTypes, type LogType, type Assignment } from '@/types/domain';
import { createLogger } from '@/lib/logger';

const log = createLogger('gps');

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', cb: () => void): void;
}

type NavigatorWithWakeLock = Navigator & {
  wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> };
};

interface ServiceWorkerRegistrationWithSync extends ServiceWorkerRegistration {
  periodicSync?: {
    register(tag: string, opts: { minInterval: number }): Promise<void>;
    getTags(): Promise<string[]>;
    unregister(tag: string): Promise<void>;
  };
}

interface OneShotOptions {
  logType?: LogType | string;
  display?: string;
  assignmentId?: string;
  tag?: string;
  additionalFields?: Record<string, string | number | null>;
}

export interface GpsFix {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export type GpsPermissionState = 'checking' | 'ready' | 'denied' | 'acquiring' | 'unavailable';

class GpsService {
  private wakeLock: WakeLockSentinelLike | null = null;
  private hiddenSince: number | null = null;
  private visibilityListenerInstalled = false;
  private routeTimer: ReturnType<typeof setInterval> | null = null;
  private routeActive = false;
  private lastFix: GpsFix | null = null;
  private lastFixAt = 0;
  // Most recent geolocation error code (1 = PERMISSION_DENIED, 2 = unavailable,
  // 3 = timeout). NOT latched — iOS Safari falsely reports code 1 even after the
  // user allows, so a later successful fix always clears it.
  private lastErrorCode: number | null = null;
  // A single long-lived watch. iOS Safari grants permission asynchronously
  // (the driver spends seconds on the prompt); a persistent watch keeps
  // listening so the fix flows in whenever it's granted, instead of a one-shot
  // getCurrentPosition that's already dead by the time the prompt is answered.
  private watchId: number | null = null;
  private subscribers = new Set<(fix: GpsFix) => void>();

  /**
   * Non-blocking snapshot of the latest GPS fix. Ensures the background watch is
   * running and returns the cached coordinates immediately (or null). NEVER
   * waits — this is what the workflow (EN ROUTE, ARRIVED, etc.) must use so a
   * slow/absent fix can never freeze the UI. The persistent watch keeps the
   * cache warm so later actions get coordinates even if the first one didn't.
   */
  peekFix(): GpsFix | null {
    this.ensureWatch();
    return this.getCachedFix(120_000);
  }

  /**
   * Capture a single GPS fix and write a log entry. Used for one-time events
   * like ARRIVED / SIGNATURE / LOAD COMPLETE / TEMPERATURE. Non-blocking: uses
   * the cached fix (best-effort, exactly like native, which logs regardless of
   * whether a GPS fix is available) and never waits on the GPS hardware.
   */
  async captureOnce(opts: OneShotOptions = {}): Promise<GpsFix | null> {
    const fix = this.peekFix();
    // Native logs the event (ARRIVED, dock load, etc.) regardless of whether a
    // GPS fix is available -- the coordinates are attached best-effort.
    await logQueueService.enqueue({
      logType: opts.logType ?? LogTypes.gpsTick,
      display: opts.display,
      assignmentId: opts.assignmentId,
      tag: opts.tag,
      latitude: fix?.latitude,
      longitude: fix?.longitude,
      accuracy: fix?.accuracy,
      additionalFields: opts.additionalFields,
    });
    return fix;
  }

  /**
   * Kick off (or keep alive) the persistent watch. Best called from a user
   * gesture (EN ROUTE / Get GPS) so iOS shows the prompt, but safe on mount too.
   */
  primeFromGesture(): void {
    this.ensureWatch();
  }

  /**
   * Subscribe to live GPS fixes. Returns an unsubscribe fn. Used by the status
   * line so it flips to "ready" the instant a fix arrives — even if the driver
   * grants permission several seconds after the screen loaded.
   */
  subscribe(cb: (fix: GpsFix) => void): () => void {
    this.subscribers.add(cb);
    this.ensureWatch();
    const cached = this.getCachedFix(30_000);
    if (cached) cb(cached);
    return () => this.subscribers.delete(cb);
  }

  getCachedFix(maxAgeMs = 120_000): GpsFix | null {
    if (!this.lastFix || Date.now() - this.lastFixAt > maxAgeMs) return null;
    return this.lastFix;
  }

  private storeFix(fix: GpsFix): void {
    this.lastFix = fix;
    this.lastFixAt = Date.now();
    this.lastErrorCode = null;
    for (const cb of this.subscribers) {
      try {
        cb(fix);
      } catch {
        /* a bad subscriber must not break the GPS pipeline */
      }
    }
  }

  /**
   * Start a single long-lived watchPosition if one isn't already running. iOS
   * Safari delivers the first fix only after the driver answers the permission
   * prompt; the persistent watch captures it whenever that happens (and keeps
   * the cache warm for the whole route). We never run getCurrentPosition
   * alongside it — iOS times those out while a watch is active.
   */
  private ensureWatch(): void {
    if (this.watchId !== null) return;
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return;
    try {
      this.watchId = navigator.geolocation.watchPosition(
        (p) =>
          this.storeFix({
            latitude: p.coords.latitude,
            longitude: p.coords.longitude,
            accuracy: p.coords.accuracy,
          }),
        (err) => {
          log.warn('watchPosition error', { code: err.code, message: err.message });
          this.lastErrorCode = err.code;
          // Keep the watch alive — iOS recovers and delivers a fix once the
          // driver grants permission, so we must not clearWatch here.
        },
        // No `timeout`: the watch must persist and deliver whenever ready.
        { enableHighAccuracy: true, maximumAge: 30_000 },
      );
    } catch (err) {
      log.warn('watchPosition threw', err);
    }
  }

  private stopWatch(): void {
    if (this.watchId !== null) {
      try {
        navigator.geolocation.clearWatch(this.watchId);
      } catch {
        /* ignore */
      }
      this.watchId = null;
    }
  }

  /**
   * Best-effort fix for log attachment. Returns the cached fix immediately when
   * recent; otherwise starts/keeps the watch and waits (bounded) for the next
   * fix. Never rejects — resolves null when none arrives in time, but the watch
   * keeps running so a later fix still lands in the cache for the next log.
   */
  async currentFix(): Promise<GpsFix | null> {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      return null;
    }
    const cached = this.getCachedFix(30_000);
    if (cached) return cached;
    this.ensureWatch();

    return new Promise<GpsFix | null>((resolve) => {
      let settled = false;
      const done = (fix: GpsFix | null) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.subscribers.delete(onFix);
        resolve(fix);
      };
      const onFix = (fix: GpsFix) => done(fix);
      this.subscribers.add(onFix);
      const timer = setTimeout(
        () => done(this.getCachedFix(120_000)),
        GpsConfig.highAccuracyTimeoutMs,
      );
    });
  }

  /** Warm permission + wait for a fix (Get GPS button — user gesture). */
  async warmUp(): Promise<GpsFix | null> {
    this.lastErrorCode = null;
    this.ensureWatch();
    return this.currentFix();
  }

  /**
   * UI helper: whether a fix is available right now. We deliberately do NOT use
   * navigator.permissions.query for geolocation — iOS Safari always reports
   * 'prompt' regardless of the real setting (Apple Developer Forums #751189).
   */
  async checkStatus(): Promise<GpsPermissionState> {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      return 'unavailable';
    }
    if (this.getCachedFix()) return 'ready';
    this.ensureWatch();
    const fix = await this.currentFix();
    if (fix) return 'ready';
    if (this.lastErrorCode === 1) return 'denied';
    return 'acquiring';
  }

  /**
   * Begin route-level GPS tracking. Mirrors the native `GpsTrackingService`:
   * every {@link GpsConfig.intervalMs} it logs a `gps break` for each active
   * assignment (local switch 2 = picked up / en route to deliver, or 5 =
   * delivering). Idempotent and self-selecting -- safe to call whenever a
   * pickup/delivery flow starts or the app resumes while logged in. Tracking
   * continues across stops until {@link stopRouteTracking} (Back to Base) or
   * until there are no active assignments left.
   */
  startRouteTracking(): void {
    // Always make sure the GPS watch is alive so the cache is warm for the very
    // first log of the next action (idempotent — safe to call repeatedly).
    this.ensureWatch();
    if (this.routeActive) return;
    this.routeActive = true;
    void this.requestWakeLock();
    this.installVisibilityListener();
    this.routeTimer = setInterval(() => {
      void this.routeTick();
    }, GpsConfig.intervalMs);
    void this.registerPeriodicBackgroundSync();
  }

  async stopRouteTracking(): Promise<void> {
    this.routeActive = false;
    if (this.routeTimer !== null) {
      clearInterval(this.routeTimer);
      this.routeTimer = null;
    }
    // Release the GPS watch when the route ends (Back to Base / no active work)
    // to save battery; it restarts on the next en-route gesture.
    this.stopWatch();
    await this.releaseWakeLock();
  }

  get isRouteTracking(): boolean {
    return this.routeActive;
  }

  /** Test-only helper: clear the tick-gap guard between cases. */
  __resetTickForTests(): void {
    this.lastTickAt = 0;
  }

  // Timestamp of the last tick that ran. Guards against duplicate `gps break`
  // rows: the interval timer (flushed late after background throttling) and
  // the tab-resume handler can otherwise fire in the same second.
  private lastTickAt = 0;

  /**
   * One tracking tick: read a fix and log `gps break` for every active
   * assignment. If nothing is active, the tracker idles (matches native, which
   * no-ops when there are no state 2/5 assignments).
   *
   * The min-gap check is set synchronously before any await so a concurrent
   * second call is rejected rather than raced.
   */
  private async routeTick(): Promise<void> {
    const now = Date.now();
    if (now - this.lastTickAt < GpsConfig.minTickGapMs) return;
    this.lastTickAt = now;
    let active: Assignment[];
    try {
      active = await assignmentRepo.getActiveAssignments();
    } catch (err) {
      log.warn('routeTick: failed to read active assignments', err);
      return;
    }
    if (active.length === 0) return;
    const fix = await this.currentFix();
    if (!fix) return;
    for (const a of active) {
      await logQueueService.enqueue({
        logType: LogTypes.gpsTick, // 'gps break'
        display: 'Display',
        assignmentId: a.remF04Id,
        tag: a.remF02Tag ?? undefined,
        latitude: fix.latitude,
        longitude: fix.longitude,
        accuracy: fix.accuracy,
      });
    }
  }

  private async requestWakeLock(): Promise<void> {
    const nav = navigator as NavigatorWithWakeLock;
    if (!nav.wakeLock) return;
    try {
      this.wakeLock = await nav.wakeLock.request('screen');
      this.wakeLock.addEventListener('release', () => {
        log.debug('wake lock released by browser');
        this.wakeLock = null;
      });
    } catch (err) {
      log.warn('wake lock request failed', err);
    }
  }

  private async releaseWakeLock(): Promise<void> {
    if (this.wakeLock && !this.wakeLock.released) {
      try {
        await this.wakeLock.release();
      } catch {
        /* ignore */
      }
    }
    this.wakeLock = null;
  }

  private installVisibilityListener(): void {
    if (this.visibilityListenerInstalled) return;
    if (typeof document === 'undefined') return;
    this.visibilityListenerInstalled = true;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState === 'hidden') {
      this.hiddenSince = Date.now();
    } else if (document.visibilityState === 'visible' && this.hiddenSince) {
      const gap = Date.now() - this.hiddenSince;
      this.hiddenSince = null;
      if (gap > GpsConfig.staleAfterMs && this.routeActive) {
        // The tab was backgrounded long enough that periodic ticks were
        // throttled/killed (platform limit). Record the gap against each
        // active assignment and refresh tracking on resume.
        let active: Assignment[];
        try {
          active = await assignmentRepo.getActiveAssignments();
        } catch {
          active = [];
        }
        // No additionalFields here: every key is passed to FileMaker verbatim
        // as a field name, and a non-existent field (the old gapMs) made FM
        // reject the whole record with "Field is missing", quarantining every
        // TRACKING GAP log fleet-wide. The gap duration has no FM field; if
        // dispatch ever wants it, Martin adds a field and we send that name.
        for (const a of active) {
          await logQueueService.enqueue({
            logType: LogTypes.trackingGap,
            display: 'Display',
            assignmentId: a.remF04Id,
            tag: a.remF02Tag ?? undefined,
          });
        }
        // Re-acquire screen wake lock (it auto-releases on hide) and tick now.
        await this.requestWakeLock();
        await this.routeTick();
      }
    }
  };

  private async registerPeriodicBackgroundSync(): Promise<void> {
    if (!('serviceWorker' in navigator)) return;
    try {
      const reg = (await navigator.serviceWorker.ready) as ServiceWorkerRegistrationWithSync;
      if (!reg.periodicSync) return;
      // Permission API check (Chrome only)
      const perm = await navigator.permissions
        // @ts-expect-error periodic-background-sync is non-standard
        .query({ name: 'periodic-background-sync' })
        .catch(() => null);
      if (perm && (perm as PermissionStatus).state !== 'granted') return;
      await reg.periodicSync.register('gps-tick', { minInterval: GpsConfig.intervalMs });
    } catch (err) {
      log.debug('periodic background sync unavailable', err);
    }
  }
}

export const gpsService = new GpsService();
